import type { CommittedDocuments } from "./types";

// Mirror of the backend's document limits (Django settings AGENT_DOCUMENT_MAX_*) and extension
// allow-list, for immediate feedback. Keep in sync; the upload endpoint stays authoritative.
export const MAX_FILE_BYTES = 4_500_000;
export const MAX_DOCS_PER_SESSION = 5;
export const MAX_TOTAL_BYTES = 22_500_000;
export const ACCEPTED_EXTENSIONS = ".pdf,.doc,.docx,.txt,.md";
export const ACCEPTED_EXTENSIONS_LABEL = "PDF, DOC, DOCX, TXT, or MD";

const ACCEPTED_EXTENSION_LIST = ACCEPTED_EXTENSIONS.split(",");

/** True when the file's extension is on the document allow-list (case-insensitive). */
export function hasAcceptedExtension(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSION_LIST.some(extension => name.endsWith(extension));
}

/** Document slots still free in the session once the committed and draft files are counted. */
export function remainingDocumentSlots(committedDocuments: CommittedDocuments, draftFiles: File[]): number {
  return MAX_DOCS_PER_SESSION - committedDocuments.count - draftFiles.length;
}

export type FileAdmission = {
  accepted: File[];
  unsupported: File[];
  oversized: File[];
  droppedBySlots: number;
  droppedByTotalSize: number;
};

/**
 * Splits `incoming` into the files that fit the allow-list and the session caps, given what the
 * session already holds, and the ones left out by reason: extension, then per-file size, then
 * count, then total size.
 */
export function admitFiles(
  incoming: File[],
  { existingCount, existingBytes }: { existingCount: number; existingBytes: number }
): FileAdmission {
  const unsupported = incoming.filter(f => !hasAcceptedExtension(f));
  const supported = incoming.filter(hasAcceptedExtension);
  const oversized = supported.filter(f => f.size > MAX_FILE_BYTES);
  const sized = supported.filter(f => f.size <= MAX_FILE_BYTES);

  const slotsLeft = MAX_DOCS_PER_SESSION - existingCount;
  const fitsCount = sized.slice(0, Math.max(0, slotsLeft));
  const droppedBySlots = sized.length - fitsCount.length;

  let runningBytes = existingBytes;
  const accepted: File[] = [];
  let droppedByTotalSize = 0;
  for (const file of fitsCount) {
    if (runningBytes + file.size <= MAX_TOTAL_BYTES) {
      accepted.push(file);
      runningBytes += file.size;
    } else {
      droppedByTotalSize++;
    }
  }

  return { accepted, unsupported, oversized, droppedBySlots, droppedByTotalSize };
}
